import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  Signal,
  viewChildren
} from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import {
  MenuBar,
  MenuBarButton,
  MenuBarDropdown,
  MenuBarDropdownContentDirective,
  MenuBarWidget
} from '@softapps/core';
import { ButtonComponent, IconComponent, TranslatePipe, translator } from '@softline/ui-core';
import { PageFilterService } from '../../services/page-filter.service';
import { FilterTemplateService } from '../../../services/filter-template.service';
import { FilterTemplate } from '../../../types/filter-template';
import { PageFilterItem } from '../../types/page-filter-item';
import { PageFilterRef } from '../../types/page-filter.ref';
import { Filter } from '../../../types/filter';
import { FilterOperator } from '../../../types/filter-operators';
import { FilterInputBase } from '../filter-inputs/filter-input.base';
import { toQueryFilter } from '../../../helper/to-query-filter.function';
import { excludeFilterNode } from '../../../helper/exclude-filter-node.function';
import { summarizeFilterValue } from '../../../helper/format-filter-value.function';

@Component({
  selector: 'soft-page-filter-menu-bar',
  imports: [
    NgComponentOutlet,
    MenuBarButton,
    MenuBarDropdown,
    MenuBarDropdownContentDirective,
    ButtonComponent,
    IconComponent,
    TranslatePipe
  ],
  templateUrl: './page-filter-menu-bar.component.html',
  styleUrl: './page-filter-menu-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFilterMenuBarComponent extends MenuBar {
  private readonly t = translator();

  readonly service = input.required<PageFilterService>();
  readonly templateService = input<FilterTemplateService | null>(null);
  readonly pageFilterRef = input.required<PageFilterRef>();
  readonly menuBarKey = input<string>('softapps/core/filter');

  override label = this.t('#CORE_FILTER.TITLE');
  override key = computed(() => this.menuBarKey());
  override items = viewChildren(MenuBarWidget);
  override disabled = model(false);

  private readonly dropdowns = viewChildren(MenuBarDropdown);
  private readonly outlets = viewChildren(NgComponentOutlet);

  readonly templateLabel = computed(() => {
    const active = this.templateService()?.displayedActive();
    if (active) return `${this.t('#CORE_FILTER.TEMPLATES.LABEL')()}: ${active.name}`;
    return `${this.t('#CORE_FILTER.TEMPLATES.TITLE')()}`;
  });

  readonly visibleItems = computed(() => {
    const service = this.service();
    return service.items.filter(
      (item) =>
        (!item.visible || item.visible()) &&
        (item.menuBar || service.getSourceItemValue(item) !== null)
    );
  });

  constructor() {
    super();
    effect(() => {
      this.templateService()
        ?.load()
        .catch((e) => console.error('[PageFilterMenuBarComponent] load templates failed', e));
    });
  }

  itemLabel(item: PageFilterItem<any, any>): string {
    const title = `${item.config.title()}`;
    const summary = summarizeFilterValue(item.config, this.service().getSourceItemValue(item));
    if (!summary) return title;
    if (summary.kind === 'count')
      return `${title}: ${summary.count} ${this.t('#CORE_FILTER.CUSTOM.SELECTED')()}`;
    return `${title}: ${summary.text}`;
  }

  itemComponent(item: PageFilterItem<any, any>) {
    return item.config.inputTypeFactory(item.config.type, this.itemOperator(item));
  }

  // itemInputs() feeds *ngComponentOutlet's [inputs], which NgComponentOutlet re-pushes via
  // setInput() on every CD cycle. A plain object literal here would be a new reference on
  // every call (e.g. one per checkbox click or scroll event inside the mounted picker), which
  // resource() treats as changed params and re-fires the loader. Cache one computed() per item
  // so unchanged content keeps returning the same reference.
  private readonly itemInputsCache = new Map<PageFilterItem<any, any>, Signal<Record<string, unknown>>>();

  itemInputs(item: PageFilterItem<any, any>): Record<string, unknown> {
    let cached = this.itemInputsCache.get(item);
    if (!cached) {
      cached = computed(
        () => ({
          item: item.config,
          itemFilter: this.service().getSourceItemValue(item),
          operator: this.itemOperator(item),
          dataService: item.source.dataService,
          queryFilter: this.itemQueryFilter(item),
        }),
        {
          // item/dataService are stable references and operator is a primitive - comparing
          // those structurally would be wrong anyway; dataService in particular holds RxJS
          // Subscription internals with circular refs that crash JSON.stringify.
          equal: (a, b) =>
            a.item === b.item &&
            a.dataService === b.dataService &&
            a.operator === b.operator &&
            JSON.stringify(a.itemFilter) === JSON.stringify(b.itemFilter) &&
            JSON.stringify(a.queryFilter) === JSON.stringify(b.queryFilter),
        }
      );
      this.itemInputsCache.set(item, cached);
    }
    return cached();
  }

  private itemOperator(item: PageFilterItem<any, any>): FilterOperator {
    const value = this.service().getSourceItemValue(item);
    return (value as { operator?: FilterOperator } | null)?.operator ?? item.operator;
  }

  private itemQueryFilter(item: PageFilterItem<any, any>) {
    const state = item.source.state;
    const value = this.service().getSourceItemValue(item);
    const tree = value ? excludeFilterNode(state.state(), value.uuid) : state.state();
    return tree ? toQueryFilter(tree, state.config) : null;
  }

  applyItem(item: PageFilterItem<any, any>): void {
    const outlet = this.outlets()[0] ?? null;
    const filterInput =
      (outlet?.componentInstance as FilterInputBase<any, FilterOperator> | null) ?? null;
    if (!filterInput) return;
    const filter: Filter<any> | null = filterInput.getFilterValue();
    this.service().applyItemFilter(item, filter, 'source');
    this.closeDropdowns();
  }

  isToggleActive(item: PageFilterItem<any, any>): boolean {
    return this.service().isToggleItemActive(item, 'source');
  }

  // menu-bar toggles commit immediately, like the item dropdowns
  toggleItem(item: PageFilterItem<any, any>): void {
    this.service().toggleItemFilter(item, 'source');
  }

  applyTemplate(template: FilterTemplate): void {
    this.templateService()?.applyAndCommit(template);
    this.closeDropdowns();
  }

  openTemplateManagement(): void {
    this.closeDropdowns();
    this.pageFilterRef().open({ view: 'templates' });
  }

  openPageFilter(): void {
    this.pageFilterRef().open();
  }

  reset(): void {
    this.service().resetFilter();
  }

  private closeDropdowns(): void {
    for (const dropdown of this.dropdowns()) {
      if (dropdown.cdkTrigger().isOpen()) dropdown.cdkTrigger().close();
      dropdown.deactivate();
    }
  }
}
